import { useEffect, useState, useCallback } from "react";
import { api } from "../api";
import {
  CASE_INTERNAL_KEYS,
  isHiddenSection,
  isHiddenSurface,
  hiddenSectionSet,
} from "./sectionMeta";

// Phase M+2b client-side permissions hook.
// Singleton, fetched once per session post-login.
//
// clientCan four-layer resolver:
//   1. isAdmin → true, unless the served map says false (193 §A: a chrome key
//      his own role switched off)
//   2. RP-of-case (only when sectionKey is case-internal) → true
//   3. grant on caseId/ccId for this sectionKey → grant.allowed
//   4. role default for sectionKey → bool
// Structural rules (creator/assignee narrowing) are server-side only.
// The hook does NOT enforce them.

const state: any = {
  data: null, // { viewer, defaults, grantsByCaseId, grantsByCcId }
  loading: false,
  error: null,
  fetched: false,
  inflight: null,
  // Hotfix #2: token-swap window flag. Set true during simulate enter/exit
  // so components that fetch from useEffect can short-circuit while the
  // JWT is being swapped and the permissions cache is being primed. Reading
  // state.swapping does NOT cause re-render — components must subscribe via
  // usePermissions() to observe transitions.
  swapping: false,
};
const subscribers = new Set<any>();
function notify() {
  for (const cb of subscribers) cb();
}

async function loadPermissions() {
  if (state.fetched && !state.error) return state.data;
  if (state.inflight) return state.inflight;
  state.loading = true;
  state.inflight = (async () => {
    try {
      const remote = await api.get("/permissions/me");
      state.data =
        remote && typeof remote === "object" && !Array.isArray(remote)
          ? remote
          : null;
      state.error = null;
      state.fetched = true;
    } catch (e) {
      state.error = e;
      // data stays null; can() falls through to deny. UI gracefully degrades.
    } finally {
      state.loading = false;
      state.inflight = null;
      notify();
    }
    return state.data;
  })();
  return state.inflight;
}

// Phase 24 — admin is the only overlay. The RP short-circuit is GONE (RP is a
// pure scope anchor; it confers no action powers). Resolution: admin → true;
// else per-case ACTION grant on the key → grant.allowed; else the role's
// view/create/edit/delete bit. Phase 24 (C3): the legacy→native alias shim is
// removed — call sites pass native keys directly.
// 193 §A — the one member of the served map the admin arm reads, named (the
// module-level `state` is untyped legacy; the ratchet's rule is a type at the
// seam, never a new baseline).
type ServedDefaults = { defaults?: Record<string, boolean> | null } | null;
function clientCan(data: any, sectionKey: any, opts: any = {}) {
  if (!data || !sectionKey) return false;
  // P20f — the VISIBILITY plane, ahead of the admin overlay, mirroring the
  // server's own ordering (permissions.can → sectionVisibility.isSectionHidden).
  // A hidden section is not a permission the viewer lacks; it is a section this
  // firm's product does not have, so it is hidden from the admin too. Every
  // gate that already asks can() therefore stops rendering it — no dead links,
  // and no affordance that would only earn a 403 from the API.
  if (isHiddenSection(data, sectionKey)) return false;
  // 193 §A (A.0, ruled 2026-09-17) — an administrator sees the app his role is
  // given. The served map (permissions.js getEffectivePermissions) carries his
  // OWN role's value for every key that decides what is DRAWN — the registry's
  // `shownUnwritten` set: task/event furniture, list columns, the five
  // view-only surfaces — and true for every RIGHT. So the overlay yields to an
  // explicit served false and to nothing else: a key the registry does not
  // know is not in the map and passes for him as it always did, and no copy
  // of the set lives here (191 §B's rule: the client reads the served map).
  if (data.viewer?.isAdmin === true) return (data as ServedDefaults)?.defaults?.[String(sectionKey)] !== false;

  const key = sectionKey;
  const { caseId, ccId } = opts;
  const isCaseInternal = CASE_INTERNAL_KEYS.has(key);

  // Per-case ACTION grant (case-internal keys only).
  if (isCaseInternal) {
    if (caseId != null) {
      const g = data.grantsByCaseId?.[caseId]?.[key];
      if (g) {
        const exp = g.expiresAt ? new Date(g.expiresAt) : null;
        if (!exp || exp > new Date()) return g.allowed === true;
      }
    }
    if (ccId != null) {
      const g = data.grantsByCcId?.[ccId]?.[key];
      if (g) {
        const exp = g.expiresAt ? new Date(g.expiresAt) : null;
        if (!exp || exp > new Date()) return g.allowed === true;
      }
    }
  }
  return data.defaults?.[key] === true;
}

/** Brief 86 §2a-2 — "financials is for the RP only, settable in Settings, or
 * granted per case in the Case Team section's access rights."
 *
 * That rule was ALREADY BUILT on the server, in permissions.js
 * isFinancialVisible(): admin · the `financial` surface scope set firm-wide in
 * Settings · responsible partner of THIS matter · a live row in
 * CaseFinancialGrant / CollectiveCaseFinancialGrant, which is exactly what the
 * Case Team panel's money toggle writes (`POST /api/financial-grants`).
 *
 * What was missing is that NOTHING ON THE CLIENT ASKED THE SAME QUESTION. The
 * two case views resolved `canFin` as `isAdmin || can("caseView.financials.view")`
 * — a role default plus a CaseAccessGrant on that section key, which is a
 * DIFFERENT TABLE from the one the server reads. The consequence was a
 * responsible partner: financially visible to every server route, and shown no
 * Financial summary at all, because RP has conferred no client-side power since
 * Phase 24 made admin the only overlay.
 *
 * So this is a mirror and not a second mechanism — the four terms below are the
 * four terms of isFinancialVisible, in the same order, over data /permissions/me
 * has been shipping since Phase 24 (`myRpCaseIds`, `myFinCaseIds`, `myRpCcIds`,
 * `myFinCcIds`, `surfaceScopes`) and which no call site had ever read.
 *
 * The server stays the wall. This decides what is DRAWN. */
// The shape /permissions/me ships, narrowed to the members this reads. The
// module-level `state` is untyped legacy; naming what THIS function needs is
// the ratchet's point — a new `any` gets a type, not a new baseline.
type MatterScope = { caseId?: number | string | null; ccId?: number | string | null };
type FinVisData = {
  viewer?: {
    isAdmin?: boolean;
    myRpCaseIds?: unknown;
    myFinCaseIds?: unknown;
    myRpCcIds?: unknown;
    myFinCcIds?: unknown;
  } | null;
  surfaceScopes?: { financial?: string } | null;
} | null;
function clientFinVisible(data: FinVisData, opts: MatterScope = {}) {
  if (!data) return false;
  if (data.viewer?.isAdmin === true) return true;
  if (data.surfaceScopes?.financial === "firm-wide") return true;
  const { caseId, ccId } = opts;
  const has = (arr: unknown, id: number | string) =>
    Array.isArray(arr) && arr.some((x) => Number(x) === Number(id));
  if (caseId != null) {
    if (has(data.viewer?.myRpCaseIds, caseId)) return true;
    if (has(data.viewer?.myFinCaseIds, caseId)) return true;
  }
  if (ccId != null) {
    if (has(data.viewer?.myRpCcIds, ccId)) return true;
    if (has(data.viewer?.myFinCcIds, ccId)) return true;
  }
  return false;
}

/* 150 §5.3 — the BILLING-DELEGATE mirror, clientFinVisible's own pattern: a
 * mirror of a server predicate that clientCan structurally cannot express.
 *
 * `caseView.timeEntries.markBilled` is HELPER-GATED (permissions.js
 * HELPER_GATED → isBillingDelegate: admin · RP-of-matter · live per-case
 * ACTION grant). clientCan cannot answer it: it has no RP term, and its
 * role-default fallthrough reads Role.defaults — where the staging DB carries
 * markBilled=true for EVERY role (verified 2026-08-30), rows no server gate
 * reads. The hosts compensated with `admin || (can(markBilled) && isRPHere)`,
 * which keeps the blanket defaults from arming everyone but also EXCLUDES the
 * per-case delegate the key exists to name — a granted non-RP delegate had
 * every server right and no mark strip. This mirror is the four terms of
 * isBillingDelegate in the server's order, deliberately WITHOUT the
 * role-default fallthrough, over data /permissions/me already ships.
 *
 * The grant arm reads caseId only — isBillingDelegate's own shape (its grant
 * lookup has no ccId arm); a faithful mirror does not widen it. The server
 * stays the wall; this decides what is DRAWN. */
type TeDelegateData = {
  viewer?: { isAdmin?: boolean; myRpCaseIds?: unknown; myRpCcIds?: unknown } | null;
  grantsByCaseId?: Record<string, Record<string, { allowed?: boolean; expiresAt?: string | null }>> | null;
} | null;
function clientIsBillingDelegate(data: TeDelegateData, opts: MatterScope = {}) {
  if (!data) return false;
  if (isHiddenSection(data, "caseView.timeEntries.markBilled")) return false;
  if (data.viewer?.isAdmin === true) return true;
  const { caseId, ccId } = opts;
  const has = (arr: unknown, id: number | string) =>
    Array.isArray(arr) && arr.some((x) => Number(x) === Number(id));
  if (caseId != null && has(data.viewer?.myRpCaseIds, caseId)) return true;
  if (ccId != null && has(data.viewer?.myRpCcIds, ccId)) return true;
  if (caseId != null) {
    const g = data.grantsByCaseId?.[String(caseId)]?.["caseView.timeEntries.markBilled"];
    if (g) {
      const exp = g.expiresAt ? new Date(g.expiresAt) : null;
      if (!exp || exp > new Date()) return g.allowed === true;
    }
  }
  return false;
}

export function usePermissions() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const rerender = () => setTick((t) => t + 1);
    subscribers.add(rerender);
    if (!state.fetched && !state.inflight) loadPermissions();
    return () => {
      subscribers.delete(rerender);
    };
  }, []);

  const can = useCallback(
    (sectionKey: any, opts?: any) => clientCan(state.data, sectionKey, opts),
    [],
  );
  /** P20f — does this firm's product lack the section entirely?
   *
   * can() already answers false for a hidden key, so a gate written as
   * `can(k)` needs nothing. This exists for the gates written as
   * `isAdmin || can(k)`: that shape short-circuits can() for administrators,
   * and hiding is precisely the rule an administrator does NOT override.
   * Those sites read `!hidden(k) && (isAdmin || can(k))`.
   *
   * Before the permissions payload lands, `hidden` is false for everything —
   * so a gate keeps whatever it did during the load window, and this adds no
   * flicker. */
  const hidden = useCallback(
    (sectionKey: any) => isHiddenSection(state.data, sectionKey),
    [],
  );
  /** P21-zero — the same question asked by SURFACE id (`ju`, `lib`, `cl`, …)
   * rather than by section key, for the four places that render a surface and
   * know it only by its short id: the sidebar row, the page mount, the search
   * chip and the report tile. sectionMeta.SURFACE_SECTION_KEY is the one map
   * they now share; before it, each of those four decided for itself, and
   * three of them decided wrong. */
  const hiddenSurface = useCallback(
    (surfaceKey: any) => isHiddenSurface(state.data, surfaceKey),
    [],
  );
  /** Brief 86 §2a-2 — the client mirror of the server's isFinancialVisible.
   * Takes { caseId } or { ccId }; see clientFinVisible above for why it is a
   * mirror rather than a can() call. */
  /** 150 §5.3 — the markBilled key's own authority (admin · RP · live grant),
   * for the (b) marking group's hint. See clientIsBillingDelegate above for
   * why this is a mirror and not `can(markBilled)`. */
  const billingDelegate = useCallback(
    (opts?: MatterScope) => clientIsBillingDelegate(state.data, opts),
    [],
  );
  const finVisible = useCallback(
    (opts?: MatterScope) => clientFinVisible(state.data, opts),
    [],
  );
  // 193 §A — one definition: the stream handler's refetchPermissions below.
  const refetch = useCallback(() => refetchPermissions(), []);

  return {
    viewer: state.data?.viewer || null,
    defaults: state.data?.defaults || {},
    // P20f — the resolved hide list, exposed for the few call sites that read
    // `defaults` directly rather than going through can() (the roles matrix, the
    // Case Team columns). can() already denies these; this lets a surface that
    // ENUMERATES keys drop them instead of showing a row nobody can ever tick.
    hiddenSections: hiddenSectionSet(state.data),
    /* 174 §6.6 — THE SURFACE SCOPES, EXPOSED. `/permissions/me` has shipped
       them since Phase 24 and only `clientFinVisible` read them, internally.
       They are the mechanism that REPLACED `Role.firmWideVisibility`
       (`schema.prisma:2493`), and three surfaces went on reading the dropped
       field instead — `undefined === true`, permanently restrictive. Two of
       the three needed no scope at all once measured (the server had already
       applied it to their rows); the third is a SENTENCE about the rule, and a
       sentence has to read the value rather than re-derive it. */
    surfaceScopes:
      (state as { data?: { surfaceScopes?: Record<string, string> } | null })
        .data?.surfaceScopes ?? null,
    grantsByCaseId: state.data?.grantsByCaseId || {},
    grantsByCcId: state.data?.grantsByCcId || {},
    loading: state.loading,
    error: state.error,
    swapping: state.swapping,
    can,
    hidden,
    hiddenSurface,
    finVisible,
    billingDelegate,
    refetch,
  };
}

/** Hotfix #2 — mark the token-swap window. Components that fire fetches
 * from useEffect should check `usePermissions().swapping` and bail while
 * true to avoid 401-burst when the simulated token is replaced with the
 * fresh admin token (or vice versa). */
export function markSwapping(value: any) {
  state.swapping = !!value;
  notify();
}

/** Test-only / login-handshake helper: reset the singleton between user sessions. */
export function __resetPermissionsCache() {
  state.data = null;
  state.loading = false;
  state.error = null;
  state.fetched = false;
  state.inflight = null;
  notify();
}

/** Force the initial fetch — call from the post-login flow so the first
 * subscriber doesn't race the boot sequence. */
export function primePermissions() {
  return loadPermissions();
}

/** 193 §A — the fetch the `access` stream event promised. `primePermissions`
 * answers the CACHED map once one has landed (loadPermissions returns
 * state.data while `fetched` is true), so a roles save — which the server
 * publishes as `access` — never reached a browser that had already loaded its
 * map; the switchboard's own proof needed a reload. This is the hook's
 * `refetch`, callable from the stream handler outside React. */
export function refetchPermissions() {
  state.fetched = false;
  state.error = null;
  return loadPermissions();
}

/** Imperative read for non-React code paths. */
export function getPermissionsSnapshot() {
  return state.data;
}
